import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Gem, Building2, User, Trophy, Medal, X } from "lucide-react";
import { useRealtimeTick } from "@/lib/realtime";
import { UserAvatar } from "@/components/UserAvatar";
import { fmtNum } from "@/lib/helpers";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function presets() {
  const t = new Date(), y = t.getFullYear(), m = t.getMonth();
  const mon = new Date(t); mon.setDate(t.getDate() - ((t.getDay() + 6) % 7));
  return [
    ["today", "Bugün", iso(t), iso(t)],
    ["week", "Bu Hafta", iso(mon), iso(t)],
    ["month", "Bu Ay", iso(new Date(y, m, 1)), iso(t)],
    ["last", "Geçen Ay", iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))],
    ["year", "Bu Yıl", iso(new Date(y, 0, 1)), iso(t)],
  ];
}

function RankBadge({ r, pts }) {
  const c = pts > 0 && { 1: "bg-yellow-400 text-slate-900", 2: "bg-slate-300 text-slate-900", 3: "bg-amber-700 text-white" }[r];
  return c ? <span className={`h-7 w-7 rounded-full inline-flex items-center justify-center text-xs font-black ${c}`}><Medal size={14} /></span>
    : <span className="h-7 w-7 inline-flex items-center justify-center text-sm font-bold text-slate-400 tabular-nums">{r}</span>;
}

function Kpi({ label, value, sub, icon: Icon, tone, testId }) {
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass p-4 animate-fade-up" data-testid={testId}>
      <div className="text-[10px] uppercase font-mono text-slate-500 flex items-center gap-1.5"><Icon size={12} /> {label}</div>
      <div className={`font-display text-3xl font-black mt-1 ${tone}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </Card>
  );
}

function RankTable({ rows, columns, kind, total, onRow, activeId, meId, testId }) {
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase font-mono text-slate-500 border-b border-slate-800">
          <th className="px-3 py-2.5 text-left w-12">Sıra</th>
          <th className="px-3 py-2.5 text-left">{kind === "branch" ? "Şube" : "Personel"}</th>
          {columns.map((c) => <th key={c.id} className="px-3 py-2.5 text-right whitespace-nowrap"><span className="inline-block h-2 w-2 rounded-full mr-1" style={{ background: c.color || "#64748b" }} />{c.name}</th>)}
          <th className="px-3 py-2.5 text-right">Adet</th>
          <th className="px-3 py-2.5 text-right">YPP Puanı</th>
          <th className="px-3 py-2.5 w-32">Pay</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => {
            const id = kind === "branch" ? r.branch_id : r.user_id;
            const pct = total ? (r.points / total) * 100 : 0;
            return (
              <tr key={id} onClick={() => onRow?.(r)} data-testid={`${testId}-row-${id}`}
                  className={`border-b border-slate-800/60 transition-colors ${onRow ? "cursor-pointer hover:bg-slate-800/40" : ""} ${activeId === id ? "bg-blue-600/10" : ""} ${meId === id ? "bg-amber-500/10" : ""}`}>
                <td className="px-3 py-2"><RankBadge r={r.rank} pts={r.points} /></td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2.5 min-w-[160px]">
                    {kind === "branch" ? <Building2 size={16} className="text-blue-300" /> : <UserAvatar path={r.photo_path} name={r.name} className="h-8 w-8" />}
                    <div><div className="font-semibold">{r.name}{meId === id && <span className="ml-1.5 text-[10px] text-amber-300">(sen)</span>}</div>
                      <div className="text-[10px] text-slate-500">{kind === "branch" ? r.code : r.branch_name || "-"} · {fmtNum(r.count)} işlem</div></div>
                  </div>
                </td>
                {columns.map((c) => <td key={c.id} className="px-3 py-2 text-right tabular-nums text-slate-300">{r.groups[c.id] ? fmtNum(r.groups[c.id]) : "—"}</td>)}
                <td className="px-3 py-2 text-right tabular-nums text-slate-400">{fmtNum(r.qty)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-display font-black text-lg text-blue-300" data-testid={`${testId}-points-${id}`}>{fmtNum(r.points)}</td>
                <td className="px-3 py-2"><div className="h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-blue-500" style={{ width: `${Math.min(pct, 100)}%` }} /></div>
                  <div className="text-[10px] text-slate-500 text-right mt-0.5">%{pct.toFixed(1)}</div></td>
              </tr>
            );
          })}
          {rows.length === 0 && <tr><td colSpan={columns.length + 5} className="text-center py-10 text-slate-500">Bu dönemde puan yok</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

export default function Ypp() {
  const { user } = useAuth();
  const ps = useMemo(presets, []);
  const [range, setRange] = useState({ key: "month", start: ps[2][2], end: ps[2][3] });
  const [data, setData] = useState(null);
  const [branchF, setBranchF] = useState(null);
  const tick = useRealtimeTick((m) => m.path.startsWith("/api/sales"));
  useEffect(() => { http.get(`/ypp?start=${range.start}&end=${range.end}`).then((r) => setData(r.data)).catch(() => {}); }, [range, tick]);
  const setDate = (k, v) => v && setRange({ ...range, key: "custom", [k]: v });

  const emps = data ? data.employees.filter((e) => !branchF || e.branch_id === branchF.branch_id) : [];
  const totalBranch = data?.branches.reduce((s, b) => s + b.points, 0) || 0;
  const totalEmp = emps.reduce((s, e) => s + e.points, 0);

  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Gem className="text-blue-400" /> Yatırımcı Performans Primi</h1>
          <p className="text-slate-400 text-sm mt-1">Ürün puanlarına göre şube ve personel sıralaması, hedeften bağımsız</p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="ypp-range">
          {ps.map(([k, l, s, e]) => (
            <button key={k} onClick={() => setRange({ key: k, start: s, end: e })} data-testid={`ypp-preset-${k}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${range.key === k ? "bg-blue-600 border-blue-500 text-white" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>{l}</button>
          ))}
          <Input type="date" value={range.start} onChange={(e) => setDate("start", e.target.value)} className="h-8 w-36 bg-slate-900/60 border-slate-700 text-xs" data-testid="ypp-start" />
          <Input type="date" value={range.end} onChange={(e) => setDate("end", e.target.value)} className="h-8 w-36 bg-slate-900/60 border-slate-700 text-xs" data-testid="ypp-end" />
        </div>
      </header>
      {!data ? <div className="text-slate-400">Yükleniyor...</div> : (
        <>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {data.company && <Kpi label="Şirket Geneli YPP" value={fmtNum(data.company.points)} sub={`${fmtNum(data.company.qty)} adet · ${fmtNum(data.company.count)} işlem`} icon={Gem} tone="text-blue-300" testId="ypp-company" />}
            {data.my_branch && <Kpi label="Şubem" value={fmtNum(data.my_branch.points)} sub={`${data.my_branch.name} · ${data.my_branch.rank}. sıra`} icon={Building2} tone="text-emerald-300" testId="ypp-my-branch" />}
            {data.me && <Kpi label="Benim Puanım" value={fmtNum(data.me.points)} sub={`${data.me.rank}. sıra / ${data.employees.length || "-"}`} icon={User} tone="text-amber-300" testId="ypp-me" />}
            {data.columns[0] && <Kpi label="En Çok Puan Getiren Gam" value={data.columns[0].name} sub={`${fmtNum(data.columns[0].points)} puan`} icon={Trophy} tone="text-yellow-300 text-xl" testId="ypp-top-group" />}
          </div>
          {data.branches.length > 0 && (
            <section className="space-y-2">
              <div className="text-xs font-mono uppercase text-slate-400 flex items-center gap-1.5"><Building2 size={13} /> Şube Sıralaması {data.scope === "all" && <span className="normal-case text-slate-500">· personel listesini filtrelemek için şubeye tıklayın</span>}</div>
              <RankTable rows={data.branches} columns={data.columns} kind="branch" total={totalBranch} testId="ypp-branches"
                         onRow={data.scope === "all" ? (r) => setBranchF(branchF?.branch_id === r.branch_id ? null : r) : null}
                         activeId={branchF?.branch_id} meId={user?.branch_id} />
            </section>
          )}
          <section className="space-y-2">
            <div className="text-xs font-mono uppercase text-slate-400 flex items-center gap-2"><User size={13} /> Personel Sıralaması
              {branchF && <button onClick={() => setBranchF(null)} className="normal-case flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-600/20 text-blue-200" data-testid="ypp-branch-filter-clear">{branchF.name} <X size={11} /></button>}
            </div>
            <RankTable rows={emps} columns={data.columns} kind="employee" total={totalEmp} testId="ypp-employees" meId={user?.id} />
          </section>
        </>
      )}
    </div>
  );
}
